/* Dispatch 3.5 -- public reset-password page.
 *
 * Route: /reset-password?token=<url-safe-token>  (no auth required)
 * Flow:  read token from query -> user types new password twice ->
 *        POST /api/auth/reset-password/consume -> redirect to /login.
 */
import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Eye, EyeOff, ShieldCheck, AlertTriangle, Lock } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

export default function ResetPasswordConsume() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(false);

  const canSubmit = pw.length >= 4 && pw === confirmPw && token && !submitting;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    setErr(null);
    try {
      await api.post("/auth/reset-password/consume", { token, new_password: pw });
      setOk(true);
      toast.success("Password updated. Please sign in with your new password.");
      setTimeout(() => nav("/login", { replace: true }), 1400);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Reset failed";
      setErr(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const tokenMissing = !token;

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#040D1C] via-[#031028] to-[#003771] text-white py-10 px-4"
      data-testid="reset-password-page">
      <div className="max-w-md mx-auto flex items-center gap-3 mb-8">
        <Logo size={32} />
        <div>
          <div className="font-heading text-lg uppercase tracking-[0.14em] text-white">Sharda Energy OS</div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/50">Reset password</div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="max-w-md mx-auto glass-elevated p-8 space-y-6">
        <div className="text-center space-y-1">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-primary flex items-center justify-center">
            <ShieldCheck className="h-7 w-7 text-white" />
          </div>
          <div className="font-heading text-xl md:text-2xl uppercase tracking-[0.08em]">Set a new password</div>
          <p className="text-sm text-white/60">Pick something strong; your policy will validate.</p>
        </div>

        {tokenMissing && (
          <div className="rounded-xl bg-rose-500/10 border border-rose-500/30 p-4 flex gap-2 text-rose-200 text-sm"
            data-testid="reset-token-missing">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">Reset link is missing</div>
              <div className="text-xs opacity-80">The token was not found in the URL. Try the link from your email again or ask HR to resend.</div>
            </div>
          </div>
        )}

        {!tokenMissing && (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
            <div>
              <Label className="text-xs uppercase tracking-widest">New password</Label>
              <div className="relative mt-1">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <Input type={showPw ? "text" : "password"} value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="pl-9 pr-9 bg-white/5"
                  data-testid="reset-new-password"
                  autoComplete="new-password" />
                <button type="button" onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50" data-testid="reset-pw-toggle">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Confirm new password</Label>
              <div className="relative mt-1">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <Input type={showPw ? "text" : "password"} value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  className="pl-9 bg-white/5"
                  data-testid="reset-confirm-password"
                  autoComplete="new-password" />
              </div>
              {confirmPw && confirmPw !== pw && (
                <div className="text-[11px] text-rose-300 mt-1" data-testid="reset-pw-mismatch">Passwords don&apos;t match</div>
              )}
            </div>

            {err && (
              <div className="rounded-xl bg-rose-500/10 border border-rose-500/30 p-3 text-sm text-rose-200"
                data-testid="reset-error">
                {err}
              </div>
            )}

            {ok && (
              <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-3 text-sm text-emerald-200"
                data-testid="reset-success">
                Password updated. Redirecting to sign-in...
              </div>
            )}

            <Button type="submit" disabled={!canSubmit || ok}
              className="w-full h-12 bg-gradient-primary text-white font-heading tracking-[0.2em] rounded-2xl"
              data-testid="reset-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "UPDATE PASSWORD"}
            </Button>
          </form>
        )}

        <div className="text-center text-xs text-white/50">
          <Link to="/login" className="hover:text-sky" data-testid="reset-back-to-login">Back to sign-in</Link>
        </div>
      </motion.div>
    </div>
  );
}
